import { Blockquote, Loading, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import ExcelUploader from "components/Shared/ExcelUploader/ExcelUploader"
import useCommon from "hooks/useCommon"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSaveXlsFormMutation, useUploadXlsFormFileMutation } from "services/mfi/xlsFormSchemas"
import { XlsFormResult } from "interfaces/xlsForm"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { XlsFormStatus } from "constants/constants"
import ValidationFormSchema from "pages/XlsForm/ValidationFormSchema"
import { FieldsData } from "interfaces/mappingInterfaces"
import XlsFormFieldMapping from "components/XlsForm/XlsFormFieldMapping"

export default function MFIAddBaseXlsSchema() {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()
  const [name, setName] = useState("")
  const [file, setFile] = useState<File>()
  const [errorSaveXls, setErrorSaveXls] = useState<XlsFormResult | null>(null)
  const [fieldsData, setFieldsData] = useState<FieldsData>({
    questionLabelPosition: 0,
    choiceLabelPosition: 0,
  })

  const [saveXls, { data: fileHeaders, reset }] = useUploadXlsFormFileMutation()
  const [saveXlsForm, { isLoading }] = useSaveXlsFormMutation()

  const handleCreate = async () => {
    if (!name || !file || !(fieldsData.questionLabelPosition && fieldsData.choiceLabelPosition)) {
      showSnackBar("Please fill all required fields", "warning")
      return
    }

    try {
      await saveXlsForm({
        baseXlsFormSchemaId: 0,
        adm0code: 0,
        name,
        file,
        questionLabelPosition: fieldsData.questionLabelPosition,
        choiceLabelPosition: fieldsData.choiceLabelPosition,
      }).unwrap()
      showSnackBar("Base XLS schema added successfully!")
      navigate(window.location.pathname.replace("/add", ""))
    } catch (err) {
      const error = err as FetchBaseQueryError

      if (error.status === XlsFormStatus.ValidationError) {
        setErrorSaveXls(error.data as XlsFormResult)
      } else showSnackBar("An error occurred while processing your request", "error")
    }
  }

  const handleClear = () => {
    setFile(undefined)
    reset()
    setFieldsData({ questionLabelPosition: 0, choiceLabelPosition: 0 })
    setErrorSaveXls(null)
  }

  const handleFileUpload = async (fileUpload: File): Promise<void> => {
    try {
      const formData = new FormData()
      formData.append("file", fileUpload)
      await saveXls(formData).unwrap()
    } catch (error) {
      handleClear()
      showSnackBar("An error occurred while processing your request", "error")
    }
  }

  useEffect(() => {
    if (file) handleFileUpload(file)
  }, [file])

  return (
    <div>
      <div className="space-y-5 w-6/12">
        <TextInput
          labelText="Name"
          name="name"
          placeholder="Name of the xls form schema"
          type="text"
          onChange={e => setName(e.target.value)}
          className="border-gray-300 rounded-md text-base"
        />

        <ExcelUploader clearAll={!file} onSelect={setFile} />
        {fileHeaders && (
          <XlsFormFieldMapping
            surveyList={fileHeaders.surveyHeaders}
            choiceList={fileHeaders.choicesHeaders}
            values={fieldsData}
            setValues={setFieldsData}
          />
        )}

        {errorSaveXls?.resultMessage && (
          <Blockquote kind="error" withIcon style={{ padding: "2rem" }}>
            {errorSaveXls.resultMessage}
          </Blockquote>
        )}

        {fileHeaders && errorSaveXls && <ValidationFormSchema data={errorSaveXls} />}

        <Loading active={isLoading} />

        {fileHeaders && (
          <div className="d-flex">
            {!errorSaveXls && <Button action={handleCreate} label="Save" className="btn-full blue mr-1" />}
            <Button action={handleClear} label="Clear & Re-Upload" className="btn-full blue" />
          </div>
        )}
      </div>
    </div>
  )
}
